/**
 * AppHeader — Shared authenticated page header.
 *
 * Shows logo, primary nav links, current user, org selector, and logout.
 * Org selector is shown when the user belongs to at least one organization,
 * letting them switch billing context between personal and team subscriptions.
 */

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, LayoutDashboard, FileText, BookOpen, User, Building2, ChevronDown, Menu /* Users — re-enable with ambassador link */ } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgContext } from '@/contexts/OrgContext';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useOrganizations } from '@/hooks/useOrganizations';
import { formatTierName } from '@/lib/utils';
import { TrafficLightLogo } from '@/components/TrafficLightLogo';
import { ModelSelector } from '@/components/ModelSelector';
import { SubscriptionBanner } from '@/components/SubscriptionBanner';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/reports', label: 'Reports', icon: FileText },
  { to: '/docs', label: 'Docs', icon: BookOpen },
];

export function AppHeader() {
  const { logout, token, user } = useAuth();
  const { data: userInfo } = useUserInfo();
  const { pathname } = useLocation();
  const { activeOrgId, setActiveOrg } = useOrgContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const authToken = token ?? '';

  const { data: orgs = [] } = useOrganizations(authToken);

  const displayName =
    userInfo?.user?.name?.trim() ||
    user?.name?.trim() ||
    userInfo?.user?.email?.split('@')[0] ||
    user?.email?.split('@')[0] ||
    'Account';
  const planName = formatTierName(userInfo?.subscription?.tier, userInfo?.subscription?.tierName);
  const personalLabel = planName ? `Personal · ${planName}` : 'Personal';
  const activeOrgName = activeOrgId
    ? (orgs.find((o) => o.id === activeOrgId)?.displayName ??
      orgs.find((o) => o.id === activeOrgId)?.name ??
      'Org')
    : personalLabel;

  return (
    <>
    <header className="no-print sticky top-0 z-50 border-b border-zinc-800/60 bg-[rgb(5,5,5)]/90 backdrop-blur-md">
      <div className="container mx-auto px-4 py-3 flex items-center gap-6">
        {/* Mobile hamburger — visible below md breakpoint */}
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="md:hidden flex items-center justify-center w-9 h-9 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-700/50 transition-colors"
          aria-label="Open navigation menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Logo */}
        <Link to="/dashboard" className="flex items-center">
          <TrafficLightLogo size="sm" showSub />
        </Link>

        {/* Primary nav — left-aligned toward logo */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV.map(({ to, label, icon: Icon }) => {
            const active = pathname === to || (to !== '/dashboard' && pathname.startsWith(to));
            return (
              <Link
                key={to}
                to={to}
                className={[
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors',
                  active
                    ? 'bg-zinc-700 text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-700/50',
                ].join(' ')}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            );
          })}
          {/* Ambassador link hidden until feature is validated on dev
          {user?.roles?.includes('admin') && (
            <Link
              to="/admin/ambassadors"
              className={[
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors',
                pathname.startsWith('/admin/ambassadors')
                  ? 'bg-zinc-700 text-white'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-700/50',
              ].join(' ')}
            >
              <Users className="w-4 h-4" />
              Ambassadors
            </Link>
          )}
          */}
        </nav>

        {/* Right side: org selector + user + logout */}
        <div className="flex items-center gap-3 ml-auto">
          {/* Org selector — only shown when the user belongs to at least one org */}
          {orgs.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden sm:flex border-zinc-700 bg-zinc-800/50 text-zinc-300 hover:bg-zinc-700/80 gap-1.5"
                >
                  <Building2 className="w-4 h-4 text-blue-400" />
                  <span className="max-w-[120px] truncate">{activeOrgName}</span>
                  <ChevronDown className="w-3 h-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-zinc-800 border-zinc-700 min-w-[180px]"
              >
                <DropdownMenuItem
                  onClick={() => setActiveOrg(null)}
                  className={`text-sm cursor-pointer ${!activeOrgId ? 'text-white font-medium' : 'text-zinc-300'}`}
                >
                  <User className="w-4 h-4 mr-2" />
                  {personalLabel}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-700" />
                {orgs.map((org) => (
                  <DropdownMenuItem
                    key={org.id}
                    onClick={() => setActiveOrg(org.id)}
                    className={`text-sm cursor-pointer ${activeOrgId === org.id ? 'text-white font-medium' : 'text-zinc-300'}`}
                  >
                    <Building2 className="w-4 h-4 mr-2 text-blue-400" />
                    {org.displayName ?? org.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <ModelSelector />

          <Link
            to="/account"
            className={[
              'flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm transition-colors',
              pathname === '/account'
                ? 'bg-zinc-700 text-white'
                : 'text-zinc-300 hover:text-white hover:bg-zinc-700/50',
            ].join(' ')}
          >
            <User className="w-4 h-4 shrink-0" />
            <span className="hidden sm:flex sm:flex-col sm:leading-none">
              <span>{displayName}</span>
              {planName && (
                // TODO: replace hardcoded text-green-400 with text-primary or hsl(var(--traffic-green))
                <span className="text-[10px] text-green-400 font-medium mt-0.5">{planName}</span>
              )}
            </span>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={logout}
            className="border-zinc-700 bg-zinc-800/50 text-white hover:bg-zinc-700/80"
            aria-label="Logout"
          >
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </div>

      {/* Mobile navigation drawer */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="bg-[rgb(10,10,10)] border-zinc-800 w-72 p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-zinc-800">
            <SheetTitle className="text-white text-left">
              <TrafficLightLogo size="sm" showSub />
            </SheetTitle>
          </SheetHeader>

          <nav className="flex flex-col px-4 py-4 gap-1" aria-label="Mobile navigation">
            {NAV.map(({ to, label, icon: Icon }) => {
              const active = pathname === to || (to !== '/dashboard' && pathname.startsWith(to));
              return (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={[
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    active
                      ? 'bg-zinc-700 text-white'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800',
                  ].join(' ')}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </Link>
              );
            })}
            {/* Ambassador link hidden until feature is validated on dev
            {user?.roles?.includes('admin') && (
              <Link
                to="/admin/ambassadors"
                onClick={() => setMobileMenuOpen(false)}
                className={[
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  pathname.startsWith('/admin/ambassadors')
                    ? 'bg-zinc-700 text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800',
                ].join(' ')}
              >
                <Users className="w-4 h-4" />
                Ambassadors
              </Link>
            )}
            */}
          </nav>

          <div className="border-t border-zinc-800 px-4 py-4 space-y-1">
            <Link
              to="/account"
              onClick={() => setMobileMenuOpen(false)}
              className={[
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                pathname === '/account'
                  ? 'bg-zinc-700 text-white'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800',
              ].join(' ')}
            >
              <User className="w-4 h-4" />
              <div className="flex flex-col leading-none">
                <span>{displayName}</span>
                {planName && (
                  // TODO: replace hardcoded text-green-400 with text-primary or hsl(var(--traffic-green))
                  <span className="text-[10px] text-green-400 font-medium mt-1">{planName}</span>
                )}
              </div>
            </Link>

            {/* Org selector in mobile menu */}
            {orgs.length > 0 && (
              <div className="px-3 py-2">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-2">Billing Context</p>
                <button
                  onClick={() => { setActiveOrg(null); setMobileMenuOpen(false); }}
                  className={[
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors',
                    !activeOrgId ? 'text-white font-medium bg-zinc-800' : 'text-zinc-400 hover:text-white',
                  ].join(' ')}
                >
                  <User className="w-3 h-3" />
                  {personalLabel}
                </button>
                {orgs.map((org) => (
                  <button
                    key={org.id}
                    onClick={() => { setActiveOrg(org.id); setMobileMenuOpen(false); }}
                    className={[
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors',
                      activeOrgId === org.id ? 'text-white font-medium bg-zinc-800' : 'text-zinc-400 hover:text-white',
                    ].join(' ')}
                  >
                    <Building2 className="w-3 h-3 text-blue-400" />
                    {org.displayName ?? org.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-zinc-800 px-4 py-4">
            <button
              onClick={() => { logout(); setMobileMenuOpen(false); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-colors w-full"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </header>
    <SubscriptionBanner />
    </>
  );
}
